import classNames from "classnames";
import { NavLink } from "react-router-dom";
import { Button } from "../../../components/Button/Button.tsx";
import { Pimento } from "../../../components/Icon/assets/Pimento.tsx";
import { Icon, type IconName } from "../../../components/Icon/Icon.tsx";
import { useAppQuery } from "../../../http/useAppQuery.ts";
import {
  HOME_BRAND_KIT,
  HOME_CHARACTERS,
  HOME_OBJECTS,
  HOME_PROJECTS,
  HOME_STYLES,
  PURCHASES,
  WORKSPACES,
} from "../../../routes.ts";
import { trackEvent } from "../../../tracking/tracking.ts";
import {
  CHARACTER_MODEL_TYPE,
  OBJECT_MODEL_TYPE,
  STYLE_MODEL_TYPE,
} from "../../constants.ts";
import { useModels } from "../../hooks/useModels.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { useBoards } from "../HomeIndex/hooks/useBoards.ts";
import type { UserPurchasedContents } from "../Purchased/types.ts";
import { SOCIAL_AND_ADS_VISUALS_CATEGORY } from "../QuickActions/constants.ts";
import { quickActionsDialogStore } from "../QuickActions/stores/quickActionsDialogStore.ts";
import { BalanceCallout } from "./BalanceCallout.tsx";
import { UserMenu } from "./UserMenu.tsx";

export const HomeSidebar = () => {
  const workspaceUuid = useWorkspaceUuid();
  const { data: workspacePurchasedContents } =
    useAppQuery<UserPurchasedContents>({
      queryKey: `workspaces/${workspaceUuid}/purchased-contents`,
    });
  const { models } = useModels();
  const { userBoards } = useBoards();

  return (
    <div className="flex-col w-[240px] justify-between pt-150">
      <div className="flex-col px-300 py-150 gap-300">
        <UserMenu />
        <Button
          variant="primary"
          onClick={() => {
            trackEvent("quick_actions:open_dialog", {
              origin: "homepage_create_button",
            });
            quickActionsDialogStore.selectCategory(
              SOCIAL_AND_ADS_VISUALS_CATEGORY,
            );
            quickActionsDialogStore.open();
          }}
          iconName="Plus"
          iconPosition="left"
        >
          Create
        </Button>
        <div>
          <HomeNavigationButton
            to={`${WORKSPACES}/${workspaceUuid}/home`}
            iconName="Home"
            title="Home"
          />
          <HomeNavigationButton
            to={HOME_PROJECTS}
            iconName="FolderOpen"
            title="Projects"
            rightText={userBoards?.length.toString()}
          />
          <SidebarSeparator />
          <HomeNavigationButton
            to={HOME_BRAND_KIT}
            iconName="Library"
            title="Brand Kit"
          />
          <HomeNavigationButton
            to={HOME_STYLES}
            iconName="Droplets"
            title="Styles"
            rightText={models
              .filter((it) => it.type === STYLE_MODEL_TYPE)
              .length.toString()}
          />
          <HomeNavigationButton
            to={HOME_CHARACTERS}
            iconName="PersonStanding"
            title="Characters"
            rightText={models
              .filter((it) => it.type === CHARACTER_MODEL_TYPE)
              .length.toString()}
          />
          <HomeNavigationButton
            to={HOME_OBJECTS}
            iconName="Shirt"
            title="Objects"
            rightText={models
              .filter((it) => it.type === OBJECT_MODEL_TYPE)
              .length.toString()}
          />
          <SidebarSeparator />
          <HomeNavigationButton
            to={PURCHASES}
            iconName="ShoppingBasket"
            title="Purchased images"
            rightText={(
              workspacePurchasedContents?.contents.length ?? 0
            ).toString()}
          />
        </div>
      </div>
      <div className="flex-col p-300 gap-200">
        <BalanceCallout />
        <div className="px-200 py-300">
          <Pimento size={90} />
        </div>
      </div>
    </div>
  );
};

const HomeNavigationButton = ({
  to,
  title,
  iconName,
  rightText,
}: {
  to: string;
  iconName: IconName;
  title: string;
  rightText?: string;
}) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      classNames(
        "h-800 pl-150 pr-200 flex-row items-center justify-between rounded-150",
        isActive
          ? "bg-surface-primary-active label-md-semibold text-primary"
          : "label-md-default text-secondary stroke-secondary-rest hover:bg-surface-primary-hover",
      )
    }
    end
  >
    {({ isActive }) => (
      <>
        <div className="flex-row-center gap-150 ">
          <Icon
            name={iconName}
            className={
              isActive ? "stroke-secondary-active" : "stroke-secondary-rest"
            }
          />
          <div>{title}</div>
        </div>
        {rightText && <div>{rightText}</div>}
      </>
    )}
  </NavLink>
);

const SidebarSeparator = () => (
  <div className="flex-col justify-center px-150 h-500">
    <div className="h-025 border-t" />
  </div>
);
